// VariantCard.js
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  IconButton,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DimensionsAndWeightInputWithUnit from "../../components/dimension-input";
import CustomTypography from "../../globalStyles/fonts";
import SubSkuCard from "./sub-sku-card";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { countryService } from "../../utils/services/country.service";
import CurrencyTextField from "../currency-field/CurrencyTextField";

const VariantCard = ({
  variant,
  index,
  handleToggleExpand,
  expanded,
  handleVariantChange,
  handleBundleToggle,
  handleRemoveSubSku,
  handleSubSkuChange,
  onChangeSubDimensionAndWeight,
  updateSubSku,
  handleProductUpdate,
  removeVariant,
  handleAddSubSku,
  onChangeDimensionAndWight,
  formErrors = {},
}) => {
  return (
    <Card
      style={{
        marginBottom: 10,
        backgroundColor: "var(--bg-paper)",
        marginTop: 10,
        border: "1px solid var(--border-color)",
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontFamily="poppins" fontWeight={500}>
            {variant.title || `Main SKU ${index + 1}`}
            {(variant.isBundle || variant.isMaster) && (
              <span style={{ color: "#DC585C" }}>
                {` (Sub-SKU: ${variant.subSkus?.length})`}
              </span>
            )}
          </Typography>

          <Box>
            <IconButton onClick={() => removeVariant(index)}>
              <DeleteIcon />
            </IconButton>
            <IconButton onClick={() => handleToggleExpand(index)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {expanded && (
          <>
            <Divider />
            <Grid container spacing={2.5} mt={1}>
              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  Title <span style={{ color: "red" }}>*</span>
                </CustomTypography>
                <FormControl fullWidth>
                  <TextField
                    sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                    type="text"
                    size="small"
                    placeholder="Enter Title"
                    value={variant.title}
                    onChange={(e) =>
                      handleVariantChange(index, "title", e.target.value)
                    }
                    error={!!formErrors?.[index + "_title"]}
                    helperText={formErrors?.[index + "_title"] || ""}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  SKU <span style={{ color: "red" }}>*</span>
                </CustomTypography>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  type="text"
                  size="small"
                  placeholder="Enter SKU"
                  value={variant.sku}
                  onChange={(e) =>
                    handleVariantChange(index, "sku", e.target.value)
                  }
                  fullWidth
                  error={!!formErrors?.[index + "_sku"]}
                  helperText={formErrors?.[index + "_sku"] || ""}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  HS Code
                </CustomTypography>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  size="small"
                  placeholder="Enter HS Code"
                  value={variant.hs_code}
                  onChange={(e) =>
                    handleVariantChange(index, "hs_code", e.target.value)
                  }
                  error={!!formErrors?.[index + "_hs_code"]}
                  helperText={formErrors?.[index + "_hs_code"] || ""}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  HTS Code
                </CustomTypography>
                <TextField
                  sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                  size="small"
                  placeholder="Enter HTS Code"
                  value={variant.hts_code}
                  onChange={(e) =>
                    handleVariantChange(index, "hts_code", e.target.value)
                  }
                  fullWidth
                  error={!!formErrors?.[index + "_hts_code"]}
                  helperText={formErrors?.[index + "_hts_code"] || ""}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  Country of Origin
                </CustomTypography>
                <AutocompleteWithPagination
                  hideLabel
                  selectedItem={
                    variant.country_of_origin
                      ? {
                          code2: `${variant?.country_of_origin}`,
                        }
                      : { code2: "" }
                  }
                  customLabel="Country of Origin"
                  onDataChange={(value) =>
                    handleVariantChange(
                      index,
                      "country_of_origin",
                      value ? value.code2 : "",
                    )
                  }
                  width="100%"
                  optionKey={(country) =>
                    `${country.code2} ${
                      country.name ? `(${country.name})` : ""
                    }`
                  }
                  apiEndPoint={(limit, skip, search = "") => {
                    const query = `$limit=${limit}&$skip=${skip}`;
                    return countryService.fetch(search, query, "code2");
                  }}
                  errorMessage={formErrors?.[index + "_country_of_origin"]}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 400 }}
                  fontSize={14}
                >
                  Price (USD) <span style={{ color: "red" }}>*</span>
                </CustomTypography>
                <FormControl fullWidth>
                  <CurrencyTextField
                    sx={{ marginTop: 0.5, backgroundColor: "var(--bg-paper)" }}
                    size="small"
                    placeholder="Enter Price"
                    value={variant.price}
                    onChange={(value) =>
                      handleVariantChange(index, "price", value)
                    }
                    error={!!formErrors?.[index + "_price"]}
                    helperText={formErrors?.[index + "_price"] || ""}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12}>
                <DimensionsAndWeightInputWithUnit
                  dimensions={{
                    height: variant.height,
                    length: variant.length,
                    width: variant.width,
                  }}
                  weight={variant.weight}
                  onChange={(e) =>
                    onChangeDimensionAndWight(index, e.field, e.value)
                  }
                  dimensionUnitValue={variant.height_unit}
                  weightUnitValue={variant.weight_unit}
                  error={{
                    height: formErrors[`${index}_height`] || "",
                    width: formErrors[`${index}_width`] || "",
                    length: formErrors[`${index}_length`] || "",
                    dimension_unit: formErrors[`${index}_dimension_unit`] || "",
                    weight: formErrors?.[index + "_weight"] || "",
                    weight_unit: formErrors[`${index}_weight_unit`] || "",
                  }}
                />
              </Grid>
            </Grid>

            {/* ── Keys Section ── */}
            <Box sx={{ mt: 2.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CustomTypography
                  style={{ textAlign: "left", fontWeight: 500 }}
                  fontSize={14}
                >
                  Extra Attributes
                </CustomTypography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    handleVariantChange(index, "option", [
                      ...(variant.option || []),
                      { option_type: "", value: "" },
                    ])
                  }
                  sx={{
                    textTransform: "none",
                    fontFamily: "poppins",
                    fontSize: 13,
                  }}
                >
                  Add Attribute
                </Button>
              </Box>

              {(variant.option || []).map((k, ki) => (
                <Grid
                  container
                  spacing={1.5}
                  key={ki}
                  alignItems="flex-start"
                  sx={{ mb: 1 }}
                >
                  <Grid item xs={12} sm={5}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Option Type"
                      value={k.option_type || ""}
                      onChange={(e) => {
                        const updated = [...(variant.option || [])];
                        updated[ki] = {
                          ...updated[ki],
                          option_type: e.target.value,
                        };
                        handleVariantChange(index, "option", updated);
                      }}
                      sx={{ backgroundColor: "var(--bg-paper)" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Value"
                      value={k.value || ""}
                      onChange={(e) => {
                        const updated = [...(variant.option || [])];
                        updated[ki] = { ...updated[ki], value: e.target.value };
                        handleVariantChange(index, "option", updated);
                      }}
                      sx={{ backgroundColor: "var(--bg-paper)" }}
                    />
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updated = (variant.option || []).filter(
                          (_, i) => i !== ki,
                        );
                        handleVariantChange(index, "option", updated);
                      }}
                      sx={{ color: "error.main", mt: 0.25 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              {(variant.option || []).length === 0 && (
                <Typography
                  fontSize="12px"
                  fontFamily="poppins"
                  color="var(--text-tertiary)"
                >
                  No attributes added.
                </Typography>
              )}
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={variant.isMaster}
                      checked={variant.isBundle}
                      onChange={(e) =>
                        handleBundleToggle(index, e.target.checked, "isBundle")
                      }
                    />
                  }
                  label="Bundle"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={variant.isBundle}
                      checked={variant.isMaster}
                      onChange={(e) =>
                        handleBundleToggle(index, e.target.checked, "isMaster")
                      }
                    />
                  }
                  label="Master"
                />
              </Box>
            </Box>

            {(variant.isBundle || variant.isMaster) &&
              Array.isArray(variant.subSkus) &&
              variant.subSkus.map((subSku, subIndex) => (
                <SubSkuCard
                  key={subIndex}
                  subSku={subSku}
                  variant={variant}
                  variantIndex={index}
                  subIndex={subIndex}
                  selectedClient={variant.selectedClient}
                  handleRemoveSubSku={handleRemoveSubSku}
                  handleSubSkuChange={handleSubSkuChange}
                  onChangeSubDimensionAndWeight={onChangeSubDimensionAndWeight}
                  updateSubSku={updateSubSku}
                  handleProductUpdate={handleProductUpdate}
                  formErrors={formErrors}
                />
              ))}

            {(variant.isBundle || variant.isMaster) && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="text"
                  onClick={() => handleAddSubSku(index, variant.isMaster)}
                  sx={{
                    color: "#0038FF",
                    textTransform: "none",
                    fontFamily: "poppins",
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  + Add Child Variant
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VariantCard;
